import React from 'react';
import { SectionHeading } from '../components/shared/SharedComponents';
import { Database, Cpu, ShieldCheck, Scale } from 'lucide-react';

const MethodologyPage = () => {
  return (
    <main className="container mx-auto px-6 pt-32 pb-24 max-w-5xl animate-in fade-in duration-1000">
      <SectionHeading 
        title="Audit Methodology" 
        subtitle="The deterministic framework behind Vyay's strategic recommendations."
        align="left"
      />

      <div className="grid grid-cols-1 gap-12 mt-16">
        <section className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary"><Database size={24} /></div>
            <h2 className="text-3xl font-serif italic">1. Data Ingestion & Normalization</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Vyay utilizes a strict schema for tool inputs, ensuring that engineering team size, seat counts, and monthly expenditure are normalized across diverse vendor categories. We prioritize raw usage data over marketing estimations to ensure the audit base is accurate to the penny.
          </p>
        </section>

        <section className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary"><Cpu size={24} /></div>
            <h2 className="text-3xl font-serif italic">2. Deterministic Reasoning Engine</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Unlike "AI-driven" tools that guess savings, Vyay uses a rule-based engine. Every recommendation is generated by checking your input against a verified matrix of tiered pricing. If a team of 5 is on a 'Pro' plan but a 'Team' plan is 30% cheaper for that volume, our engine flags it instantly. No hallucinations, just math.
          </p>
          <div className="bg-foreground/5 p-8 rounded-3xl border border-foreground/5 font-mono text-sm space-y-2">
            <p className="text-primary font-bold opacity-70">// Logic Sample</p>
            <p>if (tool.users &gt;= plan.minUsers &amp;&amp; tool.monthlySpend &gt; plan.marketRate) {"{"}</p>
            <p className="pl-6">recommend("Downgrade to " + plan.name, savings);</p>
            <p>{"}"}</p>
          </div>
        </section>

        <section className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary"><ShieldCheck size={24} /></div>
            <h2 className="text-3xl font-serif italic">3. Stochastic Synthesis (LLM Layer)</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            We use **Gemini 2.5 Flash** as a secondary layer to provide executive context. The LLM does not perform the calculations; it translates our deterministic results into a strategic narrative for engineering leadership. This "AI-on-Data" approach ensures high authority with zero risk of fiscal error.
          </p>
        </section>

        <section className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary"><Scale size={24} /></div>
            <h2 className="text-3xl font-serif italic">4. Verification & Market Rates</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Our market data is sourced directly from public vendor documentation. We maintain a conservative bias in our recommendations - favoring stability over aggressive, high-risk migrations.
          </p>
        </section>
      </div>

      <div className="mt-24 p-12 bg-primary/5 rounded-[3rem] border border-primary/10 text-center space-y-6">
        <h3 className="text-2xl font-serif italic">"Calm Engineering" for Financial Health.</h3>
        <p className="text-muted-foreground max-w-2xl mx-auto italic text-sm">
          Vyay's methodology prioritizes organizational trust. We provide the data, you provide the leadership.
        </p>
      </div>
    </main>
  );
};

export default MethodologyPage;
